import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { BackButton, NavBar } from '../../../components/layout/NavBar'
import { Services } from '@/services/services'
import { useNavigate, useParams } from 'react-router-dom'

import './FailureList.module.css'
import { CurrentSession } from '@/services/repository/session'
import { MainLayout, MainPageLayout } from '@/components/layout/MainLayout'
import { useLogin } from '@/context/login'
import { MachineFailureTypes } from '@/services/repository/machines'
import { formatDate, formatDateOnly } from '@/common/time'
import { MachinePath } from './Common'
import { Machine } from '@/services/model/machine'
import { Translates } from '@/services/repository/i18n'
import { useModal } from '@/components/modal/Modal'
import { GalleryPopup } from '@/components/GalleryPopup/gallery'

export interface NewMachineupProps {
  services: Services,
  session: CurrentSession,
  isOpen: boolean,
  machineId: string | undefined
  onClose: () => void
}

interface Image {
  url: string
}

type FailureRow = {
  id: string; // UUID of the failure
  category: string
  description: string; // Description of the failure
  type: number; // Type of failure
  detectionDate: string; // Date of detection (nullable)
  commencementDate: Date | null; // Date of commencement (nullable)
  servicemanEmail: string | null; // Email of the serviceman (nullable)
  servicemanName: string | null; // Name of the serviceman (nullable)
  reportingUserEmail: string | null; // Email of the reporting user
  reportingUserName: string | null; // Name of the reporting user
  images: Image[] | null
  opened?: boolean,
  form: {
    symptoms: string[]
  }
};

export const CommunalMachineFailures = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const machineId = useParams().mid
  // const [translates, setTranslates] = useState<Translates>()
  const [translates, setTranslates] = useState<Translates>()
  const [failures, setFailures] = useState<FailureRow[]>()
  const [machine, setMachine] = useState<Machine>()


  useEffect(() => {
    if (session) {
      setTranslates(session.translates)
      return
    }
    services.i18n.get('main').then(setTranslates)
  }, [session])

  async function refresh(): Promise<FailureRow[]> {
    return services.session.fetchWithToken(`/api/communal/machines/byid/${machineId}/failures`).then((response) => {
      return response.json()
    }).then((data) => {
      data.sort((a, b) => new Date(b.detectionDate).getTime() - new Date(a.detectionDate).getTime());
      setFailures(data)
      return data
    })
  }

  useEffect(() => {
    refresh()
  }, [machineId])

  useEffect(() => {
    setMachine(undefined)
    fetch(`/api/communal/machines/byid/${machineId}`).then((response) => {
      return response.json()
    }).then((data) => {
      setMachine(data)
    })
  }, [machineId])

  if (translates === undefined || machine === undefined || translates === undefined || failures === undefined || machineId === undefined) {
    return <div className='spinner'></div>
  }

  return <MainPageLayout pageName='communal-machine-failure' services={services} translates={translates} session={session} path={[...MachinePath({ session, translates, machine }), {
    title: session.translates.for(`page.machinefailures.title`)
  }]} navbarLeft={<BackButton services={services} translates={translates} replaceUrl={`/communal/machine/${machineId}`} />} >

    <h2 className='page-title'>{translates.for(`failures.list.title`)}</h2>

    {failures && failures.length === 0 ? <div className='empty-msg'>{translates.for(`failures.list.empty_list`)}</div> : <div className='failure-list'>
      {failures?.map((failure, index) => <>
        <div className={`failure-row ${failure.commencementDate ? 'failure-done' : 'failure-open'}`}
          onClick={() => {
            const updatedFailures = failures.map((f, i) => (
              i === index ? { ...f, opened: !f.opened } : f
            ))
            setFailures(updatedFailures)
          }}>
          <div className='col date'>{formatDate(new Date(failure.detectionDate))}</div>
          <div className='col category'><img src={`/public/img/failures/${failure.category}.svg`} /></div>
        </div>
        {failure.opened === true ? <div className='failure-details'>
          <div className='data'>
            <div className='data-item'>
              <div className='label'>{translates.for('page.machinefailures.reportingRow')}</div>
              <div className='value'>{failure.detectionDate ? formatDate(new Date(failure.detectionDate)) : '-'}</div>
            </div>
            { failure.reportingUserName ? <div className='data-item'>
              <div className='label'>{translates.for('page.machinefailures.reporting_username')}</div>
              <div className='value'>{failure.reportingUserName}</div>
            </div> : null }
            { failure.reportingUserEmail ? <div className='data-item'>
              <div className='label'>{translates.for('page.machinefailures.reporting_email')}</div>
              <div className='value'>{failure.reportingUserEmail}</div>
            </div> : null }
            {failure.commencementDate != null ? <>
              <div className='data-item'>
                <div className='label'>{translates.for('page.machinefailures.commencementRow')}</div>
                <div className='value'>{failure.commencementDate ? formatDate(new Date(failure.commencementDate)) : '-'}</div>
              </div>
            </> : null}
            {failure.servicemanName != null ? <>
              <div className='data-item'>
                <div className='label'>{translates.for('page.machinefailures.serviceman_name')}</div>
                <div className='value'>{failure.servicemanName ? failure.servicemanName : '-'}</div>
              </div>
            </> : null}
            {failure.servicemanEmail != null ? <>
              <div className='data-item'>
                <div className='label'>{translates.for('page.machinefailures.serviceman_email')}</div>
                <div className='value'>{failure.servicemanEmail ? failure.servicemanEmail : '-'}</div>
              </div>
            </> : null}
            <div className='data-item'>
              <div className='label'>{translates.for('page.machinefailures.descriptionTitle')}</div>
              <div className='value'>
                {failure.description}
                {failure.form.symptoms ? <div className='failure-form-list'>
                  {failure.form.symptoms.map(row => translates.for(`failures.form.${failure.category}.${row}`)).join(', ')}
                </div> : null }
               </div>
            </div>
          </div>
          <div className='images'>
            {failure.images?.map((image, index) => <div className='image-row' onClick={() => openModal((props) => GalleryPopup({
              ...props, 
              images: failure.images?.map(row => row.url),
              index
            }))}><img src={image.url} /></div>)}
        </div>


          {failure.commencementDate == null ? <div className='main-gray-btn' onClick={() => {
          services.session.fetchWithToken(`/api/communal/machines/byid/${machineId}/failures/${failure.id}/close`, {
            method: 'POST'
          }).then(async () => {
            return refresh().then((data: FailureRow[]) => {
              const openFailure = data.filter((row) => row.commencementDate == null)
              if (openFailure.length === 0) {
                navigate(`/communal/failures/map`)
              }
            })
          })
        }}>{translates.for('page.machinefailures.closeBtn')}</div> : null}

      </div > : null}

    </>)}
  </div>
}



  </MainPageLayout >
}

